import React from 'react';
import { Link } from 'react-router-dom';
import BlueCallToAction from '../components/common/BlueCallToAction';

/**
 * HealthcareClaimsPage component for healthcare claims and denials management
 * Provides information about industry challenges and solutions
 */
const HealthcareClaimsPage = () => {
  // Healthcare claims challenges data
  const challenges = [
    {
      title: "Medicare Advantage Billing Practice Reforms",
      category: "Medicare",
      severity: "high",
      description: "CMS Administrator promises scrutiny of Medicare Advantage program amid allegations of widespread billing issues and inappropriate denials.",
      impact: "Significant financial impact on providers and patients",
      solutions: ["Enhanced audit procedures", "Billing practice reforms", "Improved oversight"],
      source: "Becker's Hospital Review"
    },
    {
      title: "Medicaid Funding Restrictions and Cost-Cutting",
      category: "Medicaid",
      severity: "critical",
      description: "Reconciliation bill with substantial Medicaid cost-cutting measures affecting healthcare providers and patient access to care.",
      impact: "Reduced funding and increased administrative burden",
      solutions: ["Policy advocacy", "Alternative funding strategies", "Operational efficiency improvements"],
      source: "HFMA"
    },
    {
      title: "Insurance Business Consolidation and Market Exits",
      category: "Market",
      severity: "high",
      description: "Health systems shuttering insurance businesses due to financial pressures and market challenges.",
      impact: "Reduced competition and limited patient options",
      solutions: ["Market analysis", "Strategic partnerships", "Alternative models"],
      source: "Becker's Hospital Review"
    },
    {
      title: "Revenue Cycle Denial Prevention",
      category: "Operations",
      severity: "high",
      description: "Industry focus on preventing denials through provider and payer alignment and improved processes.",
      impact: "Improved cash flow and reduced administrative costs",
      solutions: ["AI-powered pre-authorization", "Real-time eligibility verification", "Enhanced documentation"],
      source: "HFMA"
    }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              <span style={{ color: '#FFC000' }}>Healthcare Claims</span> & Denials
              <br />
              Management
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 text-gray-700">
              Understanding industry challenges and implementing solutions for improved
              revenue cycle management. Our AI-powered platform provides clarity
              for effective decision making and financial goal achievement.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/demo"
                style={{ 
                  backgroundColor: '#0E3056',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  transition: 'background-color 0.2s'
                }}
                className="hover:bg-opacity-90"
              >
                Request Demo
              </Link>
              <Link
                to="/consultation"
                style={{ 
                  border: '2px solid #0E3056',
                  color: '#0E3056',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                className="hover:bg-[#0E3056] hover:text-white"
              >
                Schedule Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Industry Impact & Opportunities
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Administrative Cost Reduction */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="text-4xl text-primary-500">📊</div>
                <Link 
                  to="/solutions/automation"
                  style={{ color: '#FFC000' }}
                  className="hover:opacity-80 font-medium"
                >
                  Learn more →
                </Link>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                35% Administrative Cost Reduction Potential
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                AI automation can eliminate redundant tasks and streamline revenue cycle workflows.
              </p>
            </div>

            {/* Claims Adjudication Costs */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="text-4xl text-primary-500">💰</div>
                <Link 
                  to="/solutions/claims-management"
                  style={{ color: '#FFC000' }}
                  className="hover:opacity-80 font-medium"
                >
                  Learn more →
                </Link>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                $25.7B Annual Claims Adjudication Costs
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Healthcare industry spends billions on manual claims processing and
                rework due to denials.
              </p>
            </div>

            {/* Revenue Cycle Improvement */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="text-4xl text-primary-500">📈</div>
                <Link 
                  to="/solutions/revenue-cycle"
                  style={{ color: '#FFC000' }}
                  className="hover:opacity-80 font-medium"
                >
                  Learn more →
                </Link>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                22% Revenue Cycle Improvement Target
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Industry benchmark for optimizing days in AR and clean claim rates
                through technology.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Current Industry Challenges Section - Styled like Specialists page */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Current Industry Challenges
          </h2>
          <div className="max-w-4xl mx-auto space-y-8">
            {challenges.map((challenge, index) => (
              <div 
                key={index} 
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm"
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {challenge.title}
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    challenge.severity === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                    challenge.severity === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }`}>
                    {challenge.severity.charAt(0).toUpperCase() + challenge.severity.slice(1)}
                  </span>
                </div>
                
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {challenge.description}
                </p>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Impact:</h4>
                  <p className="text-gray-600 dark:text-gray-300">{challenge.impact}</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Solutions:</h4>
                  <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                    {challenge.solutions.map((solution, idx) => (
                      <li key={idx}>{solution}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="inline-block text-sm bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 px-3 py-1 rounded-full">
                    {challenge.category}
                  </span>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Source: {challenge.source}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Key Benefits of Modern Claims Management
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center p-6">
              <div className="text-4xl mb-4 text-primary-500">📈</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Improved Revenue Cycle
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Reduce denial rates and accelerate reimbursement through intelligent automation.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl mb-4 text-primary-500">⚡</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Enhanced Efficiency
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Streamline workflows and reduce administrative burden on staff.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl mb-4 text-primary-500">✓</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Better Compliance
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Stay ahead of regulatory requirements with automated monitoring.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <BlueCallToAction 
        heading="Ready to address these healthcare claims challenges?"
        description="Our AI-powered platform helps organizations overcome denials and optimize revenue cycles."
        primaryButtonText="Schedule Consultation"
        primaryButtonLink="/consultation"
        secondaryButtonText="Explore Solutions"
        secondaryButtonLink="/solutions"
      />
    </div>
  );
};

export default HealthcareClaimsPage;
